import { SessionsResponse, type SessionSummary } from '@diana/contracts';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAdminApi } from '../../app/AdminApiContext.js';
import { Button, useCollectionNavigation } from '../../ui/index.js';
import { formatCrmMoney } from '../crm/crm-money.js';
import { primaryClient } from '../crm/crm-format.js';
import { useRequestNotes } from '../crm/use-request-notes.js';
import { DashboardWorkSummary } from './DashboardWorkSummary.js';
import { DashboardFinance } from './DashboardFinance.js';
import { dashboardWork, lisbonDate } from './dashboard-data.js';
import './Dashboard.css';
import { StartupScreen } from '../session/StartupScreen.js';

export function CrmDashboardRoute() {
  const api = useAdminApi();
  const shoots = useQuery({
    queryKey: ['crm-shoots'],
    queryFn: () => api.get('/api/v1/admin/sessions', SessionsResponse),
  });
  if (shoots.isPending)
    return <StartupScreen title="Opening your overview" description="Preparing your workspace…" />;
  if (shoots.isError)
    return (
      <article className="crm-dashboard">
        <h1>Dashboard</h1>
        <p role="alert">Could not load your overview.</p>
        <Button variant="outline" onClick={() => void shoots.refetch()}>
          Retry
        </Button>
      </article>
    );
  return <CrmDashboard shoots={shoots.data.sessions} />;
}
export function CrmDashboard({
  shoots,
  now = new Date(),
}: {
  shoots: readonly SessionSummary[];
  now?: Date;
}) {
  const {
    value: expanded,
    setValue: setExpanded,
    rootRef,
    remember,
  } = useCollectionNavigation('crm-dashboard-position', () => ({
    allAttention: false,
    allUpcoming: false,
  }));
  const { allAttention, allUpcoming } = expanded;
  const setAllAttention = (allAttention: boolean) =>
    setExpanded((current) => ({ ...current, allAttention }));
  const setAllUpcoming = (allUpcoming: boolean) =>
    setExpanded((current) => ({ ...current, allUpcoming }));
  const work = dashboardWork(shoots, now);
  const newShoots = work.attention
    .filter((a) => a.shoot.status === 'NewRequest')
    .slice(0, allAttention ? undefined : 5)
    .map((a) => a.shoot);
  const notes = useRequestNotes(newShoots);
  const today = lisbonDate(now);
  return (
    <article
      ref={rootRef}
      onClickCapture={remember}
      onKeyDownCapture={remember}
      className="crm-dashboard"
    >
      <DashboardFinance
        shoots={shoots}
        today={today}
        pageTitle={
          <div className="crm-dashboard__page-title">
            <h1>Dashboard</h1>
          </div>
        }
        workSummary={<DashboardWorkSummary counts={work.counts} />}
      />
      <div className="crm-dashboard__work-grid">
        <section className="crm-dashboard__panel" aria-label="Upcoming shoots">
          <div className="crm-dashboard__section-heading">
            <div>
              <h2>Coming up</h2>
            </div>
            <CalendarDays size={18} aria-hidden="true" />
          </div>
          {work.upcoming.length ? (
            <ul className="crm-dashboard__work-list">
              {work.upcoming.slice(0, allUpcoming ? undefined : 5).map((shoot) => (
                <li key={shoot.id}>
                  <Link to={`/crm/shoots/${shoot.id}`}>
                    <time dateTime={shoot.scheduledStartAt ?? undefined}>
                      <strong>
                        {!shoot.scheduledStartAt
                          ? 'Date not set'
                          : lisbonDate(shoot.scheduledStartAt) === today
                            ? 'Today'
                            : new Intl.DateTimeFormat('en-GB', {
                                timeZone: 'Europe/Lisbon',
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                              }).format(new Date(shoot.scheduledStartAt!))}
                      </strong>
                      <small>
                        {shoot.scheduledStartAt
                          ? new Intl.DateTimeFormat('en-GB', {
                              timeZone: 'Europe/Lisbon',
                              hour: '2-digit',
                              minute: '2-digit',
                            }).format(new Date(shoot.scheduledStartAt))
                          : 'Scheduled'}
                      </small>
                    </time>
                    <span>
                      {shoot.title}
                      <small>{primaryClient(shoot).name}</small>
                    </span>
                    <ChevronRight size={16} aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="crm-dashboard__empty">No scheduled shoots.</p>
          )}
          {work.upcoming.length > 5 ? (
            <Button variant="text" onClick={() => setAllUpcoming(!allUpcoming)}>
              {allUpcoming ? 'Show less' : `Show all ${work.upcoming.length}`}
            </Button>
          ) : null}
        </section>
        <section className="crm-dashboard__panel" aria-label="Needs attention">
          <div className="crm-dashboard__section-heading">
            <h2>Needs attention</h2>
            <span>{work.attention.length}</span>
          </div>
          {work.attention.length ? (
            <ul className="crm-dashboard__work-list">
              {work.attention
                .slice(0, allAttention ? undefined : 5)
                .map(({ shoot, reason, outstanding }) => (
                  <li key={shoot.id}>
                    <Link to={`/crm/shoots/${shoot.id}`}>
                      <span className="crm-dashboard__attention-dot" data-stage={shoot.status} />
                      <span>
                        {shoot.title}
                        <small>
                          {reason}
                          {outstanding
                            ? ` · ${formatCrmMoney(outstanding, shoot.currency)} unpaid`
                            : ''}
                        </small>
                        {shoot.status === 'NewRequest' && notes[shoot.id] ? (
                          <small className="crm-dashboard__message">{notes[shoot.id]}</small>
                        ) : null}
                      </span>
                      <ChevronRight size={16} aria-hidden="true" />
                    </Link>
                  </li>
                ))}
            </ul>
          ) : (
            <p className="crm-dashboard__empty">Nothing needs attention right now.</p>
          )}
          {work.attention.length > 5 ? (
            <Button variant="text" onClick={() => setAllAttention(!allAttention)}>
              {allAttention ? 'Show less' : `Show all ${work.attention.length}`}
            </Button>
          ) : null}
        </section>
      </div>
    </article>
  );
}
